import { themeConfig } from '@/configs/theme.config'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Theme, LayoutType, Direction } from '@/@types/theme'
import {
    applyThemeVariant,
    modeForTheme,
    nextTheme,
    normalizeThemeName,
    type ThemeName,
} from '@/utils/themeVariant'
import {
    applyAccent,
    normalizeAccent,
    DEFAULT_ACCENT,
    type AccentName,
} from '@/utils/accent'

type ThemeState = Theme & {
    /** 5-theme palette selector (Handoff 19 §5). Layers over `mode`. */
    themeName: ThemeName
    accent: AccentName
    /** Whether each side pane sits on the tint or reads as a white panel. */
}

type ThemeAction = {
    setSchema: (payload: string) => void
    setMode: (payload: ThemeState['mode']) => void
    setSideNavCollapse: (payload: boolean) => void
    setDirection: (payload: Direction) => void
    setPanelExpand: (payload: boolean) => void
    setLayout: (payload: LayoutType) => void
    setPreviousLayout: (payload: LayoutType | '') => void
    /** Set the active theme; also syncs `mode` + applies the variant class. */
    setThemeName: (payload: ThemeName) => void
    setAccent: (payload: AccentName) => void
    /** Advance to the next theme in the cycle. */
    cycleThemeName: () => void
    /** Switch one pane; the panes are independent. */
}

const inititialThemeState: ThemeState = {
    ...themeConfig,
    themeName: themeConfig.mode === 'dark' ? 'dark' : 'light',
    accent: DEFAULT_ACCENT,
}

export const useThemeStore = create<ThemeState & ThemeAction>()(
    persist(
        (set) => ({
            ...inititialThemeState,
            setSchema: (payload) => set(() => ({ themeSchema: payload })),
            setMode: (payload) => set(() => ({ mode: payload })),
            setSideNavCollapse: (payload) =>
                set((state) => ({
                    layout: { ...state.layout, sideNavCollapse: payload },
                })),
            setDirection: (payload) => set(() => ({ direction: payload })),
            setPanelExpand: (payload) => set(() => ({ panelExpand: payload })),
            setLayout: (payload) =>
                set((state) => ({
                    layout: { ...state.layout, type: payload },
                })),
            setPreviousLayout: (payload) =>
                set((state) => ({
                    layout: { ...state.layout, previousType: payload },
                })),
            setThemeName: (payload) => {
                applyThemeVariant(payload)
                set(() => ({ themeName: payload, mode: modeForTheme(payload) }))
            },
            setAccent: (payload) => {
                applyAccent(payload)
                set(() => ({ accent: payload }))
            },
            cycleThemeName: () =>
                set((state) => {
                    const next = nextTheme(state.themeName ?? 'light')
                    applyThemeVariant(next)
                    return { themeName: next, mode: modeForTheme(next) }
                }),
        }),
        {
            name: 'theme',
            // v1: dim / forest / sepia were deleted (14 Sep 2026). Rewrite a
            // stored choice onto the theme it was derived from, once, so the
            // persisted value stops naming something that no longer exists.
            version: 1,
            migrate: (persisted, _version) => {
                const st = persisted as Partial<ThemeState> | undefined
                // Nothing stored yet: hand back the initial state rather than
                // casting undefined through ThemeState, which only typechecked
                // while the pane-surface fields kept the two shapes overlapping.
                if (!st) return { ...inititialThemeState } as ThemeState
                const live = normalizeThemeName(st.themeName)
                return {
                    ...st,
                    themeName: live,
                    mode: modeForTheme(live),
                    accent: normalizeAccent(st.accent),
                } as ThemeState
            },
            // Zustand restores the value but nothing has touched the DOM yet, so
            // without this a persisted choice is state the page does not reflect.
            onRehydrateStorage: () => (restored) => {
                if (!restored) return
                // `migrate` above has already mapped any deleted theme name;
                // this only has to make the DOM reflect it.
                applyThemeVariant(restored.themeName ?? 'light', false)
                applyAccent(normalizeAccent(restored.accent))
            },
        },
    ),
)
